import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { Order } from '../../../api/models/order.model';
import { OrderItem } from '../../../api/models/order-item.model';

export interface OrderDetailDialogData {
  order: Order;
}

@Component({
  selector: 'app-order-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    MatListModule
  ],
  template: `
    <div class="p-0">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <div>
          <h2 class="text-xl font-bold m-0">Order Details</h2>
          <p class="text-sm opacity-80 m-0">{{ data.order.invoiceNumber || '#' + data.order.id }}</p>
        </div>
        <button mat-icon-button (click)="onClose()" class="text-white">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 max-h-[70vh] overflow-y-auto">
        <!-- Order Info -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="text-xs text-gray-500 uppercase tracking-wide">Table</div>
            <div class="text-lg font-semibold text-gray-800">{{ data.order.tableNumber || 'N/A' }}</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="text-xs text-gray-500 uppercase tracking-wide">Customer</div>
            <div class="text-lg font-semibold text-gray-800">{{ data.order.customerName || 'Anonymous' }}</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="text-xs text-gray-500 uppercase tracking-wide">Status</div>
            <mat-chip [color]="getStatusColor(data.order.status)" class="mt-1">
              <mat-icon class="text-sm mr-1">{{ getStatusIcon(data.order.status) }}</mat-icon>
              {{ getDisplayStatus(data.order.status) }}
            </mat-chip>
          </div>
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="text-xs text-gray-500 uppercase tracking-wide">Created</div>
            <div class="text-lg font-semibold text-gray-800">{{ data.order.createdAt | date:'short' }}</div>
          </div>
        </div>

        <!-- Items List -->
        <div class="mb-4">
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            <mat-icon class="text-sm mr-1 align-middle">receipt</mat-icon>
            Order Items ({{ data.order.items.length || 0 }})
          </h3>
          <div class="border rounded-lg overflow-hidden">
            <div *ngFor="let item of data.order.items; let last = last" 
                 class="flex items-center justify-between p-3"
                 [class.border-b]="!last">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <span class="text-indigo-600 font-bold">{{ item.quantity }}x</span>
                </div>
                <div>
                  <div class="font-medium text-gray-800">{{ item.drinkName || 'Unknown Item' }}</div>
                  <div class="text-sm text-gray-500" *ngIf="item.specialInstructions">
                    <mat-icon class="text-xs align-middle">notes</mat-icon>
                    {{ item.specialInstructions }}
                  </div>
                </div>
              </div>
              <div class="text-right">
                <div class="font-semibold text-gray-800">\${{ item.subtotal | number:'1.2-2' }}</div>
                <div class="text-xs text-gray-500">\${{ item.unitPrice | number:'1.2-2' }} each</div>
              </div>
            </div>
            <div *ngIf="!data.order.items || data.order.items.length === 0" 
                 class="p-4 text-center text-gray-500">
              No items in this order
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div *ngIf="data.order.notes" class="mb-4">
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            <mat-icon class="text-sm mr-1 align-middle">notes</mat-icon>
            Notes
          </h3>
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-gray-700">
            {{ data.order.notes }}
          </div>
        </div>

        <!-- Payment Info -->
        <div class="mb-4" *ngIf="data.order.paymentMethod">
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            <mat-icon class="text-sm mr-1 align-middle">payment</mat-icon>
            Payment Method
          </h3>
          <div class="bg-gray-50 rounded-lg p-3">
            <span class="font-medium">{{ data.order.paymentMethod }}</span>
          </div>
        </div>

        <!-- Total -->
        <mat-divider class="my-4"></mat-divider>
        <div class="flex items-center justify-between">
          <span class="text-lg font-semibold text-gray-700">Total Amount</span>
          <span class="text-2xl font-bold text-indigo-600">\${{ data.order.total | number:'1.2-2' }}</span>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2 p-4 border-t bg-gray-50">
        <button mat-button (click)="onClose()">Close</button>
        <button mat-raised-button color="primary" *ngIf="canUpdateStatus()" (click)="onUpdateStatus()">
          <mat-icon>update</mat-icon>
          Update Status
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 400px;
      max-width: 600px;
    }
  `]
})
export class OrderDetailDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<OrderDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrderDetailDialogData
  ) {}

  onClose(): void {
    this.dialogRef.close();
  }

  onUpdateStatus(): void {
    this.dialogRef.close({ action: 'updateStatus', order: this.data.order });
  }

  canUpdateStatus(): boolean {
    const status = this.data.order.status?.toUpperCase();
    return status !== 'DELIVERED' && status !== 'CANCELLED';
  }

  getStatusColor(status: string | undefined): string {
    const normalizedStatus = status?.toUpperCase() || '';
    switch(normalizedStatus) {
      case 'PENDING': return 'warn';
      case 'CONFIRMED': 
      case 'PREPARING': return 'accent';
      case 'READY': 
      case 'DELIVERED': return 'primary';
      case 'CANCELLED': return 'warn';
      default: return 'primary';
    }
  }

  getStatusIcon(status: string | undefined): string {
    const normalizedStatus = status?.toUpperCase() || '';
    switch(normalizedStatus) {
      case 'PENDING': return 'warning';
      case 'CONFIRMED': return 'check';
      case 'PREPARING': return 'schedule';
      case 'READY': return 'check_circle';
      case 'DELIVERED': return 'local_shipping';
      case 'CANCELLED': return 'cancel';
      default: return 'help';
    }
  }

  getDisplayStatus(status: string | undefined): string {
    const normalizedStatus = status?.toUpperCase() || '';
    return normalizedStatus.charAt(0) + normalizedStatus.slice(1).toLowerCase();
  }
}
